// HOTGAMES sound engine. Every sound is synthesized with the Web Audio API (no files to load),
// so the whole set weighs a few kilobytes and works offline. Real recordings can replace any
// sound later: drop /sfx/<name>.mp3 and list it in /sfx/manifest.json — it wins over the synth.

import { AVATARS } from "@shared/palette";
import { asset } from "./asset";
import { prefs } from "./prefs";

export type SfxName =
  | "tap" | "pop" | "whoosh" | "tick" | "tock" | "ding" | "buzz" | "fanfare" | "drumroll" | "crash"
  | "fooled" | "howl" | "coins" | "join" | "flip" | "match" | "boom" | "streak" | "count" | "go"
  | "reveal" | "sting" | "correct" | "wrong" | "skip" | "timeup" | "bleep" | "sad" | "win" | "nextq"
  // avatar voices
  | "boing" | "swish" | "seeds" | "plinks" | "sizzle" | "hot" | "clink" | "woof" | "meow" | "growl"
  | "yip" | "hoot" | "baa" | "bleat" | "toast" | "spy" | "car" | "crow" | "moo";

type Pt = [number, number]; // [time offset (s), value]

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let bus: GainNode | null = null;
let wet: GainNode | null = null;
let noiseBuf: AudioBuffer | null = null;
let duckUntil = 0;
const fileBuffers = new Map<string, AudioBuffer | null>();
let manifest: Set<string> | null = null;

function ensure(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC({ latencyHint: "interactive" });
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 12;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.2;
    master = ctx.createGain();
    master.gain.value = prefs.get().volume;
    master.connect(comp).connect(ctx.destination);
    bus = ctx.createGain();
    bus.connect(master);
    // small synthetic room so everything sits in one space
    const conv = ctx.createConvolver();
    conv.buffer = impulse(ctx, 1.3, 2.6);
    wet = ctx.createGain();
    wet.gain.value = 0.22;
    wet.connect(conv).connect(master);
    noiseBuf = makeNoise(ctx, 2.5);
    prefs.subscribe(() => {
      if (master && ctx) master.gain.setTargetAtTime(prefs.get().volume, ctx.currentTime, 0.05);
    });
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** Call once from the app root: unlocks audio on the first user gesture (iOS/Android policy). */
export function installAudioUnlock() {
  const unlock = () => {
    const c = ensure();
    if (c && c.state === "running") {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    }
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
  void loadManifest();
}

async function loadManifest() {
  try {
    const r = await fetch(asset("sfx/manifest.json"), { cache: "force-cache" });
    if (!r.ok) return;
    const list = (await r.json()) as string[];
    manifest = new Set(list);
  } catch {
    manifest = new Set();
  }
}

function impulse(c: AudioContext, seconds: number, decay: number): AudioBuffer {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, len, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
}

function makeNoise(c: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

// ---------------------------------------------------------------- building blocks

interface Out {
  c: AudioContext;
  dest: AudioNode;
  t: number;
}

function env(o: Out, t0: number, peak: number, a: number, hold: number, r: number): GainNode {
  const g = o.c.createGain();
  g.gain.setValueAtTime(0.0001, o.t + t0);
  g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), o.t + t0 + Math.max(a, 0.002));
  g.gain.setValueAtTime(Math.max(peak, 0.0002), o.t + t0 + a + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, o.t + t0 + a + hold + r);
  return g;
}

function curve(param: AudioParam, o: Out, t0: number, pts: Pt[], exp = false) {
  if (!pts.length) return;
  param.setValueAtTime(pts[0][1], o.t + t0 + pts[0][0]);
  for (let i = 1; i < pts.length; i++) {
    const [dt, v] = pts[i];
    if (exp && v > 0) param.exponentialRampToValueAtTime(v, o.t + t0 + dt);
    else param.linearRampToValueAtTime(v, o.t + t0 + dt);
  }
}

function osc(o: Out, type: OscillatorType, freq: Pt[] | number, t0: number, dur: number, dest: AudioNode, detune = 0) {
  const n = o.c.createOscillator();
  n.type = type;
  n.detune.value = detune;
  if (typeof freq === "number") n.frequency.setValueAtTime(freq, o.t + t0);
  else curve(n.frequency, o, t0, freq, true);
  n.connect(dest);
  n.start(o.t + t0);
  n.stop(o.t + t0 + dur + 0.05);
  return n;
}

function noise(o: Out, t0: number, dur: number, dest: AudioNode) {
  const n = o.c.createBufferSource();
  n.buffer = noiseBuf;
  n.loop = true;
  n.connect(dest);
  n.start(o.t + t0, Math.random() * 1.5);
  n.stop(o.t + t0 + dur + 0.05);
  return n;
}

function filter(o: Out, type: BiquadFilterType, freq: Pt[] | number, q: number, t0 = 0): BiquadFilterNode {
  const f = o.c.createBiquadFilter();
  f.type = type;
  f.Q.value = q;
  if (typeof freq === "number") f.frequency.value = freq;
  else curve(f.frequency, o, t0, freq, true);
  return f;
}

function drive(o: Out, amount: number): WaveShaperNode {
  const ws = o.c.createWaveShaper();
  const n = 1024;
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = ((1 + amount) * x) / (1 + amount * Math.abs(x));
  }
  ws.curve = c;
  return ws;
}

function lfo(o: Out, rate: number, depth: number, target: AudioParam, t0: number, dur: number, fadeIn = 0) {
  const l = o.c.createOscillator();
  l.frequency.value = rate;
  const g = o.c.createGain();
  if (fadeIn > 0) {
    g.gain.setValueAtTime(0, o.t + t0);
    g.gain.linearRampToValueAtTime(depth, o.t + t0 + fadeIn);
  } else g.gain.value = depth;
  l.connect(g).connect(target);
  l.start(o.t + t0);
  l.stop(o.t + t0 + dur + 0.05);
}

/** A sung/voiced sound: sawtooth source → parallel formant filters → envelope. */
function vox(
  o: Out,
  p: {
    t0?: number;
    dur: number;
    pitch: Pt[];
    formants: { f: Pt[] | number; q: number; g: number }[];
    peak: number;
    a?: number;
    r?: number;
    vib?: { rate: number; depth: number; fadeIn?: number };
    trem?: { rate: number; depth: number };
    wave?: OscillatorType;
    breath?: number;
    open?: Pt[]; // lowpass opening (mouth)
    rough?: number;
    send?: number;
  },
) {
  const t0 = p.t0 ?? 0;
  const a = p.a ?? 0.02;
  const r = p.r ?? 0.12;
  const e = env(o, t0, p.peak, a, Math.max(0, p.dur - a - r), r);
  let head: AudioNode = e;
  if (p.rough) {
    const d = drive(o, p.rough);
    d.connect(e);
    head = d;
  }
  const sum = o.c.createGain();
  if (p.open) {
    const lp = filter(o, "lowpass", p.open, 0.7, t0);
    sum.connect(lp).connect(head);
  } else sum.connect(head);
  if (p.trem) {
    const tg = o.c.createGain();
    tg.gain.value = 1 - p.trem.depth / 2;
    lfo(o, p.trem.rate, p.trem.depth / 2, tg.gain, t0, p.dur);
    e.disconnect();
    e.connect(tg).connect(o.dest);
  } else e.connect(o.dest);
  if (p.send && wet) {
    const s = o.c.createGain();
    s.gain.value = p.send;
    e.connect(s).connect(wet);
  }
  // source → formant bank
  const pre = o.c.createGain();
  const src = osc(o, p.wave ?? "sawtooth", p.pitch, t0, p.dur, pre);
  for (const fm of p.formants) {
    const bp = filter(o, "bandpass", fm.f, fm.q, t0);
    const g = o.c.createGain();
    g.gain.value = fm.g;
    pre.connect(bp).connect(g).connect(sum);
  }
  if (p.vib) lfo(o, p.vib.rate, p.vib.depth, src.frequency, t0, p.dur, p.vib.fadeIn ?? 0);
  if (p.breath) {
    const ng = env(o, t0, p.breath, a, Math.max(0, p.dur - a - r), r);
    const bp = filter(o, "bandpass", p.formants[0]?.f ?? 800, 1.2, t0);
    noise(o, t0, p.dur, bp);
    bp.connect(ng).connect(sum);
  }
}

function tone(o: Out, type: OscillatorType, f: Pt[] | number, t0: number, peak: number, a: number, hold: number, r: number, dest?: AudioNode, detune = 0) {
  const e = env(o, t0, peak, a, hold, r);
  e.connect(dest ?? o.dest);
  osc(o, type, f, t0, a + hold + r, e, detune);
  return e;
}

function bell(o: Out, f: number, t0: number, peak: number, decay: number) {
  // inharmonic partials = glassy bell
  const parts: [number, number, number][] = [[1, 1, 1], [2.76, 0.45, 0.6], [5.4, 0.2, 0.35], [8.9, 0.08, 0.2]];
  for (const [mult, g, d] of parts) tone(o, "sine", f * mult, t0, peak * g, 0.003, 0, decay * d);
}

function brass(o: Out, freqs: number[], t0: number, dur: number, peak: number) {
  const lp = filter(o, "lowpass", [[0, 500], [0.06, 3200], [Math.max(0.1, dur), 1400]], 1.2, t0);
  const e = env(o, t0, peak, 0.025, Math.max(0, dur - 0.1), 0.12);
  lp.connect(e).connect(o.dest);
  if (wet) {
    const s = o.c.createGain();
    s.gain.value = 0.5;
    e.connect(s).connect(wet);
  }
  for (const f of freqs) {
    osc(o, "sawtooth", f, t0, dur, lp, -6);
    osc(o, "sawtooth", f, t0, dur, lp, 6);
  }
}

function hit(o: Out, t0: number, peak: number, type: BiquadFilterType, f: number, q: number, dur: number) {
  const e = env(o, t0, peak, 0.002, 0, dur);
  const fl = filter(o, type, f, q);
  noise(o, t0, dur + 0.02, fl);
  fl.connect(e).connect(o.dest);
}

/**
 * A cartoon "voice": pure tones (a sine plus a soft triangle an octave up) gliding through `pitch`,
 * like a squeaky toy or a slide whistle. Cute on purpose: no noise, no growl, no realism.
 */
function squeak(
  o: Out,
  t0: number,
  pitch: Pt[],
  dur: number,
  peak: number,
  opt: { vib?: [number, number]; trem?: [number, number]; bright?: number } = {},
) {
  const e = env(o, t0, peak, Math.min(0.02, dur / 5), Math.max(0, dur - 0.07), 0.05);
  if (opt.trem) {
    const tg = o.c.createGain();
    tg.gain.value = 1 - opt.trem[1] / 2;
    lfo(o, opt.trem[0], opt.trem[1] / 2, tg.gain, t0, dur);
    e.connect(tg).connect(o.dest);
  } else e.connect(o.dest);
  if (wet) {
    const sendGain = o.c.createGain();
    sendGain.gain.value = 0.18;
    e.connect(sendGain).connect(wet);
  }
  const base = osc(o, "sine", pitch, t0, dur, e);
  const g2 = o.c.createGain();
  g2.gain.value = opt.bright ?? 0.25;
  g2.connect(e);
  const over = osc(o, "triangle", pitch.map(([t, f]) => [t, f * 2] as Pt), t0, dur, g2);
  if (opt.vib) {
    lfo(o, opt.vib[0], opt.vib[1], base.frequency, t0, dur);
    lfo(o, opt.vib[0], opt.vib[1] * 2, over.frequency, t0, dur);
  }
}

// ---------------------------------------------------------------- the sounds

const SOUNDS: Record<SfxName, (o: Out, opt: { intensity: number }) => void> = {
  tap(o) {
    tone(o, "sine", [[0, 1300], [0.03, 760]], 0, 0.12, 0.002, 0, 0.05);
  },
  pop(o) {
    tone(o, "sine", [[0, 760], [0.09, 170]], 0, 0.45, 0.002, 0.01, 0.1);
    hit(o, 0, 0.12, "highpass", 2500, 0.7, 0.025);
  },
  whoosh(o) {
    const e = env(o, 0, 0.32, 0.12, 0.05, 0.22);
    const bp = filter(o, "bandpass", [[0, 380], [0.38, 2800]], 1.3);
    noise(o, 0, 0.42, bp);
    bp.connect(e).connect(o.dest);
  },
  tick(o) {
    hit(o, 0, 0.22, "highpass", 4200, 0.8, 0.014);
    tone(o, "sine", 2100, 0, 0.08, 0.001, 0, 0.02);
  },
  tock(o) {
    tone(o, "sine", [[0, 980], [0.05, 820]], 0, 0.28, 0.002, 0, 0.07);
    hit(o, 0, 0.12, "bandpass", 1800, 1.5, 0.02);
  },
  ding(o) {
    bell(o, 1318.5, 0, 0.32, 1.2);
    bell(o, 1975.5, 0.075, 0.26, 1.2);
  },
  buzz(o) {
    const lp = filter(o, "lowpass", 950, 0.8);
    const e = env(o, 0, 0.22, 0.01, 0.24, 0.08);
    lp.connect(e).connect(o.dest);
    osc(o, "sawtooth", 138, 0, 0.34, lp);
    osc(o, "sawtooth", 146, 0, 0.34, lp);
  },
  fanfare(o) {
    const C5 = 523.25, E5 = 659.25, G5 = 783.99, C6 = 1046.5;
    brass(o, [C5], 0, 0.12, 0.2);
    brass(o, [E5], 0.13, 0.12, 0.2);
    brass(o, [G5], 0.26, 0.12, 0.22);
    brass(o, [C5, E5, G5, C6], 0.4, 0.95, 0.16);
    hit(o, 0.4, 0.18, "highpass", 5000, 0.5, 1.2);
  },
  drumroll(o, { intensity }) {
    const dur = 1.1 + intensity * 0.6;
    const n = Math.floor(dur * 24);
    for (let i = 0; i < n; i++) {
      const t = i / 24 + (Math.random() - 0.5) * 0.006;
      const v = 0.05 + (i / n) * 0.3 * (0.75 + Math.random() * 0.25);
      hit(o, t, v, "bandpass", 1900, 0.9, 0.05);
    }
    SOUNDS.crash({ ...o, t: o.t + dur }, { intensity });
    tone(o, "sine", [[0, 110], [0.18, 48]], dur, 0.6, 0.003, 0.02, 0.3);
  },
  crash(o) {
    hit(o, 0, 0.32, "highpass", 3200, 0.4, 1.8);
    hit(o, 0, 0.16, "bandpass", 6500, 0.6, 1.2);
  },
  fooled(o) {
    // sad trombone: wah, wah, wah, waaaah (muted slide trombone, filter "wah" per note)
    const notes: [number, number, number][] = [[0, 233.08, 0.36], [0.4, 220, 0.36], [0.8, 207.65, 0.36], [1.2, 196, 1.15]];
    for (const [t0, f, d] of notes) {
      const last = d > 1;
      const lp = filter(o, "lowpass", last
        ? [[0, 420], [0.12, 1500], [0.4, 700], [0.55, 1400], [0.8, 650], [d, 380]]
        : [[0, 420], [0.1, 1600], [d, 500]], 3.5, t0);
      const e = env(o, t0, 0.24, 0.03, d - 0.12, 0.09);
      lp.connect(e).connect(o.dest);
      if (wet) {
        const s = o.c.createGain();
        s.gain.value = 0.5;
        e.connect(s).connect(wet);
      }
      const pitch: Pt[] = [[0, f * 0.97], [0.05, f], [d, last ? f * 0.985 : f]];
      const a = osc(o, "sawtooth", pitch, t0, d, lp, -5);
      const b = osc(o, "sawtooth", pitch, t0, d, lp, 5);
      if (last) {
        lfo(o, 5.6, 6, a.frequency, t0 + 0.25, d - 0.25, 0.3);
        lfo(o, 5.6, 6, b.frequency, t0 + 0.25, d - 0.25, 0.3);
      }
    }
  },
  howl(o) {
    vox(o, {
      dur: 2.1,
      wave: "triangle",
      pitch: [[0, 320], [0.5, 610], [1.35, 640], [2.1, 410]],
      formants: [{ f: [[0, 420], [0.6, 640], [2.1, 380]], q: 4, g: 1.3 }, { f: [[0, 900], [2.1, 760]], q: 6, g: 0.6 }],
      peak: 0.42,
      a: 0.28,
      r: 0.5,
      vib: { rate: 5.2, depth: 9, fadeIn: 0.8 },
      breath: 0.04,
      send: 0.9,
    });
  },
  coins(o) {
    const lp = filter(o, "lowpass", 6000, 0.7);
    lp.connect(o.dest);
    tone(o, "square", 1567.98, 0, 0.09, 0.002, 0.05, 0.04, lp);
    tone(o, "square", 2093, 0.07, 0.1, 0.002, 0.04, 0.3, lp);
    bell(o, 4186, 0.07, 0.05, 0.6);
  },
  join(o) {
    tone(o, "sine", [[0, 620], [0.12, 1250]], 0, 0.26, 0.004, 0.06, 0.1);
    SOUNDS.pop({ ...o, t: o.t + 0.12 }, { intensity: 0 });
  },
  flip(o) {
    hit(o, 0, 0.2, "bandpass", 2600, 0.9, 0.06);
    tone(o, "sine", [[0, 520], [0.05, 900]], 0.01, 0.1, 0.002, 0, 0.05);
  },
  match(o) {
    bell(o, 1318.5, 0, 0.22, 0.9);
    bell(o, 1661.2, 0.07, 0.2, 0.9);
    bell(o, 1975.5, 0.14, 0.22, 1.1);
  },
  boom(o) {
    const d = drive(o, 6);
    const e = env(o, 0, 0.9, 0.003, 0.05, 0.6);
    d.connect(e).connect(o.dest);
    osc(o, "sine", [[0, 130], [0.45, 34]], 0, 0.7, d);
    const lp = filter(o, "lowpass", [[0, 1400], [1.2, 200]], 0.8);
    const ne = env(o, 0, 0.5, 0.002, 0.05, 1.2);
    noise(o, 0, 1.3, lp);
    lp.connect(ne).connect(o.dest);
  },
  streak(o) {
    [1046.5, 1174.7, 1318.5, 1568, 1760].forEach((f, i) => tone(o, "triangle", f, i * 0.055, 0.16, 0.003, 0.02, 0.12));
  },
  count(o) {
    tone(o, "sine", 880, 0, 0.3, 0.004, 0.09, 0.06);
  },
  go(o) {
    tone(o, "sine", 1760, 0, 0.26, 0.004, 0.22, 0.2);
    tone(o, "sine", 1318.5, 0, 0.18, 0.004, 0.22, 0.2);
  },
  reveal(o) {
    const lp = filter(o, "lowpass", [[0, 260], [0.85, 2200]], 1.4);
    const e = env(o, 0, 0.16, 0.5, 0.25, 0.25);
    lp.connect(e).connect(o.dest);
    for (const f of [110, 164.8, 220]) {
      osc(o, "sawtooth", f, 0, 1.0, lp, -7);
      osc(o, "sawtooth", f, 0, 1.0, lp, 7);
    }
  },
  sting(o) {
    // original spy motif: low tremolo guitar, minor tension (E – G – B – A#)
    const notes: [number, number][] = [[0, 82.41], [0.26, 98], [0.52, 123.47], [0.78, 116.54]];
    for (const [t0, f] of notes) {
      const lp = filter(o, "lowpass", 1400, 1.5, t0);
      const g = env(o, t0, 0.3, 0.005, 0.18, t0 === 0.78 ? 0.9 : 0.12);
      const d = drive(o, 3);
      lp.connect(d).connect(g).connect(o.dest);
      if (wet) {
        const s = o.c.createGain();
        s.gain.value = 0.7;
        g.connect(s).connect(wet);
      }
      const n = osc(o, "sawtooth", f * 2, t0, t0 === 0.78 ? 1.1 : 0.3, lp);
      osc(o, "square", f, t0, t0 === 0.78 ? 1.1 : 0.3, lp);
      void n;
    }
  },
  correct(o) {
    bell(o, 1567.98, 0, 0.3, 1);
    bell(o, 2093, 0.09, 0.3, 1.2);
    SOUNDS.coins({ ...o, t: o.t + 0.05 }, { intensity: 0 });
  },
  wrong(o) {
    tone(o, "sine", [[0, 240], [0.24, 130]], 0, 0.42, 0.004, 0.08, 0.16);
    tone(o, "square", [[0, 120], [0.2, 90]], 0, 0.05, 0.004, 0.08, 0.1);
  },
  skip(o) {
    SOUNDS.whoosh(o, { intensity: 0 });
    tone(o, "triangle", [[0, 520], [0.18, 330]], 0.05, 0.16, 0.004, 0.04, 0.1);
  },
  timeup(o) {
    const lp = filter(o, "lowpass", 2600, 0.8);
    lp.connect(o.dest);
    for (let i = 0; i < 4; i++) tone(o, "square", i % 2 ? 660 : 880, i * 0.16, 0.12, 0.004, 0.09, 0.03, lp);
  },
  bleep(o) {
    tone(o, "sine", 1000, 0, 0.26, 0.006, 0.42, 0.012);
  },
  sad(o) {
    tone(o, "triangle", [[0, 392], [0.3, 370], [0.7, 311]], 0, 0.2, 0.03, 0.55, 0.25);
  },
  win(o) {
    SOUNDS.fanfare(o, { intensity: 1 });
    SOUNDS.coins({ ...o, t: o.t + 1.2 }, { intensity: 0 });
  },
  nextq(o) {
    // moving on: a quick whoosh and a two-note chime
    const bp = filter(o, "bandpass", [[0, 600], [0.25, 3500]], 1.4);
    const e = env(o, 0, 0.16, 0.08, 0.05, 0.15);
    noise(o, 0, 0.3, bp);
    bp.connect(e).connect(o.dest);
    bell(o, 1046.5, 0.2, 0.16, 0.6);
    bell(o, 1568, 0.32, 0.16, 0.8);
  },

  // -------------------------------------------------------------- avatar voices
  boing(o) {
    // khinkali wobble: "boyoyoyoing"
    const e = env(o, 0, 0.4, 0.004, 0.25, 0.4);
    e.connect(o.dest);
    const car = osc(o, "sine", [[0, 190], [0.6, 330]], 0, 0.7, e);
    const m = o.c.createOscillator();
    m.frequency.value = 15;
    const mg = o.c.createGain();
    mg.gain.setValueAtTime(110, o.t);
    mg.gain.exponentialRampToValueAtTime(1, o.t + 0.65);
    m.connect(mg).connect(car.frequency);
    m.start(o.t);
    m.stop(o.t + 0.75);
  },
  swish(o) {
    for (const t0 of [0, 0.16]) {
      const e = env(o, t0, 0.3, 0.03, 0.04, 0.12);
      const bp = filter(o, "bandpass", [[0, 3200], [0.19, 700]], 2.2, t0);
      noise(o, t0, 0.2, bp);
      bp.connect(e).connect(o.dest);
    }
  },
  seeds(o) {
    SOUNDS.pop(o, { intensity: 0 });
    for (let i = 0; i < 8; i++) {
      const t0 = 0.08 + Math.random() * 0.32;
      tone(o, "sine", 2400 + Math.random() * 1800, t0, 0.06 + Math.random() * 0.05, 0.001, 0, 0.03);
    }
  },
  plinks(o) {
    [880, 1046.5, 1318.5, 1568].forEach((f, i) => {
      tone(o, "sine", f, i * 0.085, 0.24, 0.002, 0, 0.28);
      tone(o, "sine", f * 4, i * 0.085, 0.03, 0.001, 0, 0.05);
    });
  },
  sizzle(o) {
    const hp = filter(o, "highpass", 4800, 0.7);
    const g = o.c.createGain();
    g.gain.setValueAtTime(0.0001, o.t);
    for (let i = 0; i < 30; i++) g.gain.setValueAtTime(Math.random() * 0.28, o.t + i * 0.024);
    g.gain.setValueAtTime(0.0001, o.t + 0.75);
    noise(o, 0, 0.75, hp);
    hp.connect(g).connect(o.dest);
    for (const t0 of [0.15, 0.42]) tone(o, "sine", [[0, 240], [0.06, 900]], t0, 0.22, 0.002, 0, 0.05);
  },
  hot(o) {
    const e = env(o, 0, 0.16, 0.04, 0.4, 0.1);
    e.connect(o.dest);
    const n = osc(o, "sine", [[0, 1150], [0.5, 2700]], 0, 0.55, e);
    lfo(o, 8, 28, n.frequency, 0, 0.55);
    hit(o, 0.5, 0.18, "highpass", 6000, 0.6, 0.28);
  },
  clink(o) {
    bell(o, 2650, 0, 0.18, 0.9);
    bell(o, 2790, 0.09, 0.14, 0.8);
    for (const t0 of [0.45, 0.62]) tone(o, "sine", [[0, 330], [0.12, 115]], t0, 0.32, 0.004, 0.02, 0.08);
  },
  woof(o) {
    // a puppy: "arf! arf!"
    squeak(o, 0, [[0, 700], [0.035, 1150], [0.12, 760]], 0.13, 0.34, { bright: 0.45 });
    squeak(o, 0.2, [[0, 760], [0.035, 1250], [0.12, 820]], 0.13, 0.32, { bright: 0.45 });
  },
  meow(o) {
    // a kitten: "mew~"
    squeak(o, 0, [[0, 820], [0.1, 1250], [0.38, 1050], [0.5, 760]], 0.5, 0.3, { vib: [7, 14], bright: 0.2 });
  },
  growl(o) {
    // a teddy bear: a purring "rawr" and a little "boop"
    squeak(o, 0, [[0, 210], [0.12, 330], [0.4, 240]], 0.42, 0.42, { trem: [24, 0.5], bright: 0.6 });
    tone(o, "sine", [[0, 520], [0.1, 700]], 0.5, 0.2, 0.005, 0.04, 0.08);
  },
  yip(o) {
    // a fox cub: "yip-yip-yip!", going up
    for (let i = 0; i < 3; i++) {
      squeak(o, i * 0.13, [[0, 900 + i * 120], [0.03, 1400 + i * 150], [0.09, 980 + i * 120]], 0.1, 0.28, { bright: 0.4 });
    }
  },
  hoot(o) {
    // an owlet: a soft "hoo… hoo"
    squeak(o, 0, [[0, 520], [0.2, 500]], 0.22, 0.3, { vib: [5, 6], bright: 0.1 });
    squeak(o, 0.32, [[0, 500], [0.32, 440]], 0.34, 0.3, { vib: [5, 6], bright: 0.1 });
  },
  baa(o) {
    // a lamb: a wobbly "baa-a-a"
    squeak(o, 0, [[0, 600], [0.5, 560]], 0.55, 0.32, { vib: [9, 38], trem: [9, 0.5], bright: 0.35 });
  },
  bleat(o) {
    // a little goat: "meh-eh-eh"
    squeak(o, 0, [[0, 760], [0.42, 700]], 0.45, 0.3, { vib: [13, 55], trem: [13, 0.6], bright: 0.4 });
  },
  toast(o) {
    SOUNDS.clink(o, { intensity: 0 });
    brass(o, [392], 0.85, 0.14, 0.16);
    brass(o, [523.25, 659.25], 1.0, 0.55, 0.15);
  },
  spy(o) {
    SOUNDS.sting(o, { intensity: 0 });
  },
  car(o) {
    // a toy car: a little "vroom" and "beep-beep!"
    const lp = filter(o, "lowpass", 1400, 1);
    lp.connect(o.dest);
    tone(o, "sawtooth", [[0, 110], [0.3, 230]], 0, 0.12, 0.02, 0.25, 0.08, lp);
    const horn = filter(o, "bandpass", 1400, 0.8);
    horn.connect(o.dest);
    for (const t0 of [0.42, 0.6]) {
      tone(o, "square", 660, t0, 0.16, 0.005, 0.09, 0.03, horn);
      tone(o, "square", 830, t0, 0.13, 0.005, 0.09, 0.03, horn);
    }
  },
  crow(o) {
    // a cartoon rooster on a slide whistle: "cock-a-doodle-doo!"
    squeak(o, 0, [[0, 620], [0.09, 700]], 0.1, 0.28, { bright: 0.3 });
    squeak(o, 0.13, [[0, 780], [0.09, 860]], 0.1, 0.28, { bright: 0.3 });
    squeak(o, 0.26, [[0, 940], [0.12, 1100]], 0.13, 0.3, { bright: 0.3 });
    squeak(o, 0.44, [[0, 1180], [0.18, 1320], [0.5, 1000]], 0.52, 0.3, { vib: [8, 25], bright: 0.3 });
  },
  moo(o) {
    // a calf: a curious "moo?"
    squeak(o, 0, [[0, 240], [0.3, 280], [0.55, 380]], 0.6, 0.38, { vib: [5, 5], bright: 0.5 });
  },
};

// ---------------------------------------------------------------- public API

export interface PlayOpts {
  volume?: number;
  /** 0..1 — some sounds (drumroll) scale with it */
  intensity?: number;
  delay?: number;
  /** play even if "sound effects" are off (never used for game audio) */
  force?: boolean;
}

export function play(name: SfxName, opts: PlayOpts = {}) {
  if (!opts.force && !prefs.get().sound) return;
  const c = ensure();
  if (!c || !bus) return;
  const start = c.currentTime + 0.01 + (opts.delay ?? 0);
  const ducked = performance.now() < duckUntil ? 0.45 : 1;
  const g = c.createGain();
  g.gain.value = (opts.volume ?? 1) * ducked;
  g.connect(bus);
  // the Guess microphone ignores our own sounds: mark the window for recorded files too
  markPlayed(((opts.delay ?? 0) + (DURATION[name] ?? 1.2)) * 1000);
  if (manifest?.has(name)) {
    void playFile(name, c, g, start);
    return;
  }
  try {
    SOUNDS[name]({ c, dest: g, t: start }, { intensity: opts.intensity ?? 0.5 });
  } catch (e) {
    console.warn("[sfx]", name, e);
  }
  // disconnect later to free the graph
  setTimeout(() => g.disconnect(), ((opts.delay ?? 0) + 4) * 1000);
}

async function playFile(name: string, c: AudioContext, dest: AudioNode, at: number) {
  let buf = fileBuffers.get(name);
  if (buf === undefined) {
    try {
      const r = await fetch(asset(`sfx/${name}.mp3`));
      buf = await c.decodeAudioData(await r.arrayBuffer());
    } catch {
      buf = null;
    }
    fileBuffers.set(name, buf);
  }
  if (!buf) {
    SOUNDS[name as SfxName]?.({ c, dest, t: c.currentTime }, { intensity: 0.5 });
    return;
  }
  const s = c.createBufferSource();
  s.buffer = buf;
  s.connect(dest);
  const when = Math.max(at, c.currentTime);
  markPlayed((when - c.currentTime + buf.duration) * 1000 + 120);
  s.start(when);
}

/** The avatar's own voice (cat → meow, car → vroom + honk …). */
export function avatarVoice(avatar: number, opts: PlayOpts = {}) {
  const a = AVATARS[avatar];
  if (a) play(a.sound as SfxName, opts);
}

/** Lower effects while the narrator speaks. */
export function duck(ms: number) {
  duckUntil = Math.max(duckUntil, performance.now() + ms);
}

/** Is any app sound playing right now? (the Guess microphone ignores this window) */
let lastPlayed = 0;
export function markPlayed(ms: number) {
  lastPlayed = Math.max(lastPlayed, performance.now() + ms);
}
export function appSoundActive(): boolean {
  return performance.now() < lastPlayed;
}

export function haptic(pattern: number | number[] = 12) {
  if (!prefs.get().haptics) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* not supported */
  }
}

export function audioContext(): AudioContext | null {
  return ensure();
}

export const SFX_NAMES = Object.keys(SOUNDS) as SfxName[];

/** Approximate length of each sound (s), used to keep the Guess microphone deaf to our own sounds. */
const DURATION: Partial<Record<SfxName, number>> = {
  tap: 0.1, pop: 0.2, tick: 0.1, tock: 0.15, ding: 1.3, buzz: 0.4, fanfare: 1.6, drumroll: 2.6, crash: 1.9,
  fooled: 2.6, howl: 2.4, coins: 0.6, join: 0.4, flip: 0.15, match: 1.2, boom: 1.4, streak: 0.5, count: 0.25,
  go: 0.5, reveal: 1.1, sting: 2.0, correct: 1.4, wrong: 0.45, skip: 0.5, timeup: 0.7, bleep: 0.5, sad: 0.9,
  win: 2.4, boing: 0.8, swish: 0.4, seeds: 0.5, plinks: 0.6, sizzle: 0.8, hot: 0.9, clink: 0.8, woof: 0.4,
  meow: 0.6, growl: 0.7, yip: 0.45, hoot: 0.75, baa: 0.65, bleat: 0.55, toast: 1.6, spy: 2.0, car: 0.8,
  crow: 1.0, moo: 0.7, nextq: 1.0,
};
